import * as authSelectors from '@redux/reducers/auth';
import { LatLngExpression, Map } from 'leaflet';
import 'leaflet-draw';
import debounce from 'lodash.debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MapContainer, useMapEvents } from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import * as unitActions from '../../../views/screens/Units/actions';
import * as geofenceActions from '../../screens/Geofences/actions';
import * as geofenceSelectors from '../../screens/Geofences/reducers';
import * as locationSelectors from '../../screens/Locations/reducers';
import { LOCATION_DATA_LIMIT_FOR_CLUSTERING } from '../../screens/Locations/types';
import * as routeSelectors from '../../screens/Routes/reducers/routes';
import { UNIT_DATA_LIMIT_FOR_CLUSTERING } from '../../screens/Units/types';
import MapBaseLayers, { MapBaseLayersOptions } from './components/MapBaseLayers';
import MapGeofenceEdit, { MapGeofenceEditOptions } from './components/MapGeofenceEdit';
import MapGeofences, { MapGeofencesOptions } from './components/MapGeofences';
import MapLocationEdit, { MapLocationEditOptions } from './components/MapLocationEdit';
import MapLocations, { MapLocationsOptions } from './components/MapLocations';
import MapObjectsBounds, { MapObjectsBoundsOptions } from './components/MapObjectsBounds';
import MapRouteEdit from './components/MapRouteEdit';
import MapRoutes, { MapRoutesOptions } from './components/MapRoutes';
import MapUnits, { MapUnitsOptions } from './components/MapUnits';
import MapUnitsPublic, { MapUnitsPublicOptions } from './components/MapUnitsPublic';
import RulerControl, { RulerControlOptions } from './components/RulerControl';
import ToolbarGeofencesControl, {
  GeofencesToolbarOptions
} from './components/ToolbarGeofencesControl';
import ToolbarHomeControl, { HomeToolbarOptions } from './components/ToolbarHomeControl';
import ToolbarLocationsControl, {
  LocationsToolbarOptions
} from './components/ToolbarLocationsControl';
import ToolbarRoutesControl, { RoutesToolbarOptions } from './components/ToolbarRoutesControl';
import ToolbarStreetViewControl, {
  StreetViewToolbarOptions
} from './components/ToolbarStreetViewControl';
import ToolbarTrackUnitWindow, {
  TrackUnitWindowOptions
} from './components/ToolbarTrackUnitWindow';
import ToolbarUnitLabelsControl, {
  UnitLabelsToolbarOptions
} from './components/ToolbarUnitLabelsControl';
import ToolbarUnitsControl, { UnitsToolbarOptions } from './components/ToolbarUnitsControl';
import ToolbarUnitTrajectoryControl, {
  TrajectoryToolbarOptions
} from './components/ToolbarUnitTrajectoryControl';
import { MapProvider } from './context/GoogleMapsContext';

interface MapProps {
  mapBaseLayersOptions: MapBaseLayersOptions;
  mapOptions: MapOptions;
  homeToolbarOptions: HomeToolbarOptions;
  locationsToolbarOptions: LocationsToolbarOptions;
  geofencesToolbarOptions: GeofencesToolbarOptions;
  routesToolbarOptions: RoutesToolbarOptions;
  unitsToolbarOptions: UnitsToolbarOptions;
  unitLabelsToolbarOptions: UnitLabelsToolbarOptions;
  rulerControlOptions: RulerControlOptions;
  trajectoryToolbarOptions: TrajectoryToolbarOptions;
  streetViewToolbarOptions: StreetViewToolbarOptions;
  mapLocationsOptionsToolbar: MapLocationsOptions;
  mapLocationsOptions: MapLocationsOptions;
  mapLocationEditOptions: MapLocationEditOptions;
  mapGeofencesOptionsToolbar: MapGeofencesOptions;
  mapGeofencesOptions: MapGeofencesOptions;
  mapObjectsBoundsOptions: MapObjectsBoundsOptions;
  mapGeofenceEditOptions: MapGeofenceEditOptions;
  mapRoutesOptionsToolbar: MapRoutesOptions;
  mapRoutesOptions: MapRoutesOptions;
  mapUnitsOptionsToolbar: MapUnitsOptions;
  mapUnitsOptions: MapUnitsOptions;
  mapUnitsPublicOptions?: MapUnitsPublicOptions;
  trackUnitWindowOptions?: TrackUnitWindowOptions;
  startClusteringAt?: number;
  startRerender?: any;
  setIsEditingLocation?: any;
}

interface MapOptions {
  center?: LatLngExpression;
  zoom?: number;
  style?: React.CSSProperties;
  className: string;
  rerenderMap?: Date | React.Key;
  fetchMapObjects?: any;
  resetMapHome?: any;
  showTrajectory?: boolean;
}

const MapsBasic = (props: MapProps) => {
  const zoomLevelToStartClustering = useSelector(
    (state) => authSelectors.getAuthUser(state).zoomLevelToStartClustering
  );

  const {
    mapBaseLayersOptions,
    mapOptions,
    homeToolbarOptions,
    locationsToolbarOptions,
    geofencesToolbarOptions,
    routesToolbarOptions,
    unitsToolbarOptions,
    unitLabelsToolbarOptions,
    rulerControlOptions,
    trajectoryToolbarOptions,
    streetViewToolbarOptions,
    mapLocationsOptionsToolbar,
    mapLocationsOptions,
    mapLocationEditOptions,
    mapGeofencesOptionsToolbar,
    mapGeofencesOptions,
    mapGeofenceEditOptions,
    mapRoutesOptionsToolbar,
    mapRoutesOptions,
    mapUnitsOptionsToolbar,
    mapUnitsOptions,
    mapObjectsBoundsOptions,
    mapUnitsPublicOptions,
    trackUnitWindowOptions,
    startClusteringAt = zoomLevelToStartClustering ?? 9, //Nivel de zoom determinado
    startRerender,
    setIsEditingLocation
  } = props;
  const {
    style,
    className,
    center,
    zoom,
    rerenderMap,
    fetchMapObjects,
    resetMapHome,
    showTrajectory: showTrajectoryProp
  } = mapOptions;

  const history = useHistory();
  const dispatch = useDispatch();

  const [map, setMap] = useState<Map | undefined>();
  const intl = useIntl();
  const [isSelectedSomething, setIsSelectedSomething] = useState<any>({});
  const [listenMapControlChanges, setMapControlChanges] = useState<
    undefined | (boolean | string)[]
  >(undefined);
  const [listenMapControlChangesLayers, setMapControlChangesLayers] = useState<
    undefined | (string | boolean)[]
  >(undefined);
  const [showLocations, setShowLocations] = useState(false);
  const [showGeofences, setShowGeofences] = useState(false);
  const [showRoutes, setShowRoutes] = useState(false);
  const [showUnits, setShowUnits] = useState(unitsToolbarOptions.showUnits === true);
  const [showTrajectory, setShowTrajectory] = useState(showTrajectoryProp ?? false);
  const [activateShowGeofences, setActivateShowGeofences] = useState<boolean>(false);
  const [mapInitialized, setMapInitialized] = useState<boolean>(true);
  const [zoomClusterLimit, setZoomClusterLimit] = useState<number>(startClusteringAt);
  const [isClusterLimitSet, setIsClusterLimitSet] = useState<boolean>(false);
  const [mapBounds, setMapBounds] = useState<any>({});

  const { store, geofences, routes, isFetchingLocations, isFetchingGeofences } = useSelector(
    (state) => ({
      store: state,
      //locations: locationSelectors.getLocationsListByMapBounds(state, mapBounds),
      geofences: geofenceSelectors.getGeofencesList(state),
      routes: routeSelectors.getRoutesList(state),
      isFetchingLocations: locationSelectors.isFetchingListLocations(state),
      isFetchingGeofences: geofenceSelectors.isFetchingListGeofences(state)
    })
  );

  useDeepCompareEffect(() => {
    if (
      map &&
      mapUnitsOptionsToolbar?.units &&
      mapLocationsOptionsToolbar?.locations &&
      isClusterLimitSet === false &&
      (mapLocationsOptionsToolbar?.locations?.length >= LOCATION_DATA_LIMIT_FOR_CLUSTERING ||
        mapUnitsOptionsToolbar?.units?.length >= UNIT_DATA_LIMIT_FOR_CLUSTERING)
    ) {
      setZoomClusterLimit(map?.getZoom() + 1);
      setIsClusterLimitSet(true);
    }
  }, [
    mapLocationsOptionsToolbar?.locations?.map((location) => ({
      locationId: location.locationId,
      latitude: location.latitude,
      longitude: location?.longitude,
      isShownInMap: location?.isShownInMap
    })),
    mapUnitsOptionsToolbar?.units,
    map,
    isClusterLimitSet
  ]);

  const initialMapCenter: LatLngExpression = center ||
    (authSelectors.getAuthUserMapHomeCenter(store) as LatLngExpression) || [51.5074, 0.1278];
  const initialZoom = zoom || authSelectors.getAuthUserMapHomeZoom(store) || 13;
  const [mapPosition, setMapPosition] = useState<LatLngExpression>(initialMapCenter);
  useEffect(() => {
    setMapControlChanges([
      // @ts-ignore MapQuest
      typeof MQ,
      showLocations,
      showGeofences,
      showRoutes,
      showUnits,
      showTrajectory,
      intl.locale
    ]);
  }, [showLocations, showGeofences, showRoutes, showUnits, showTrajectory, intl.locale]);

  useEffect(() => {
    // @ts-ignore MapQuest
    setMapControlChangesLayers([typeof MQ, intl.locale]);
  }, [intl.locale]);

  //Used For Editing Location
  const [editingLocation, setEditingLocation] = useState<any>(undefined);
  const [mapLocationEditOptionsToolbar, setMapLocationEditOptionsToolbar] =
    useState<any>(undefined);

  const editingLocationRef = useRef<any>(editingLocation);

  useEffect(() => {
    editingLocationRef.current = editingLocation;
    setMapLocationEditOptionsToolbar({
      location:
        editingLocationRef?.current &&
        editingLocationRef?.current.latitude != null &&
        editingLocationRef?.current.longitude != null
          ? editingLocationRef?.current
          : {
              latitude:
                editingLocationRef?.current && editingLocationRef?.current.latitude != null
                  ? editingLocationRef?.current.latitude
                  : initialMapCenter[0],
              longitude:
                editingLocationRef?.current && editingLocationRef?.current.longitude != null
                  ? editingLocationRef?.current.longitude
                  : initialMapCenter[1],
              radius:
                editingLocationRef?.current && editingLocationRef?.current.radius
                  ? editingLocationRef?.current.radius
                  : 10,
              locationClassIconName:
                editingLocationRef?.current && editingLocationRef?.current.locationClassIconName
                  ? editingLocationRef?.current.locationClassIconName
                  : 'Marker1'
            },
      onLocationDragend: (values) => {
        if (setEditingLocation)
          setEditingLocation({
            ...editingLocationRef?.current,
            longitude: values.lng,
            latitude: values.lat
          });
      },
      showLocation: editingLocationRef && editingLocationRef.current ? true : false,
      hideMarkersByZoom: false,
      useRectangleBounds: true,
      isMapFullScreen: true,
      usePopovers: true
    });
    if (setIsEditingLocation) setIsEditingLocation(editingLocationRef?.current);
  }, [editingLocation]);

  //Used For Editing Geofence
  const [editingGeofence, setEditingGeofence] = useState<any>(undefined);
  const [mapGeofenceEditOptionsToolbar, setMapGeofenceEditOptionsToolbar] =
    useState<any>(undefined);
  const editingGeofenceRef = useRef(editingGeofence);

  useEffect(() => {
    editingGeofenceRef.current = editingGeofence;
    setMapGeofenceEditOptionsToolbar({
      geofence: editingGeofenceRef?.current ? editingGeofenceRef?.current : {},
      onGeofenceEdit: (values) => {
        if (setEditingGeofence)
          setEditingGeofence({
            ...editingGeofenceRef?.current,
            ...values
          });
      },
      showGeofence: editingGeofenceRef && editingGeofenceRef.current,

      isMapFullScreen: true,
      usePopovers: true
    });
  }, [editingGeofence]);

  //Used For Editing Route
  const [editingRoute, setEditingRoute] = useState<any>(undefined);
  const editingRouteRef = useRef(editingRoute);

  useEffect(() => {
    editingRouteRef.current = editingRoute;
  }, [editingRoute]);

  //Used For Editing Unit
  const [selectedUnit, setSelectedUnit] = useState<any>(mapUnitsOptions?.selectedUnit || undefined);
  const selectedUnitRef = useRef(selectedUnit);

  useEffect(() => {
    //al cambiar unidad también vamos a limpiar el history para evitar que
    //se cargue la unidad que esta como parametro en el estado de history
    if (
      selectedUnit &&
      selectedUnit?.unitId &&
      selectedUnitRef?.current &&
      selectedUnitRef?.current?.unitId != selectedUnit?.unitId
    ) {
      // @ts-ignore reset history
      history.replace();
    }

    if (
      selectedUnit &&
      selectedUnit?.unitId &&
      !mapUnitsOptionsToolbar.initialUnitInformationOpenId
    ) {
      dispatch(unitActions.startFetchingUnit(selectedUnit?.unitId));
      dispatch(unitActions.showUnitInMap(selectedUnit?.unitId));
    } else if (mapUnitsOptionsToolbar.initialUnitInformationOpenId)
      dispatch(unitActions.showUnitInMap(selectedUnit?.unitId));

    selectedUnitRef.current = selectedUnit;
  }, [selectedUnit]);

  //Used to show Control Show All Units
  useEffect(() => {
    setShowUnits(unitsToolbarOptions.showUnits === true);
  }, [unitsToolbarOptions.showUnits]);

  // It sets a delay so it waits until the user stops dragging or zooming the map to do the fetch
  const fetchMapItemsWithBounds = useCallback(
    debounce((boundsFilters) => {
      fetchMapObjects(boundsFilters);
      setMapBounds(boundsFilters);

      if (showGeofences) {
        dispatch(
          geofenceActions.startFetchingGeofences({
            operatorId: mapGeofencesOptionsToolbar?.membership?.operatorId,
            organizationId: mapGeofencesOptionsToolbar?.membership?.organizationId,
            bounds: boundsFilters
          })
        );
        setActivateShowGeofences(true);
      }
    }, 1000),
    [
      dispatch,
      showLocations,
      mapLocationsOptionsToolbar,
      showGeofences,
      mapGeofencesOptionsToolbar,
      fetchMapObjects,
      isFetchingLocations
    ]
  );

  // Adds listeners to the map to save the bounds every time the map area changes
  useEffect(() => {
    if (map && (showLocations || showGeofences)) {
      const handleMapMovement = () => {
        const bounds = map.getBounds();
        const boundsFilter = {
          minLat: bounds.getSouthWest().lat,
          minLng: bounds.getSouthWest().lng,
          maxLat: bounds.getNorthEast().lat,
          maxLng: bounds.getNorthEast().lng,
          showInMapLocations: showLocations,
          showInMapGeofences: showGeofences,
          mapZoomLevel: map.getZoom()
        };

        fetchMapItemsWithBounds(boundsFilter);
      };
      // Attach event listeners
      map.on('moveend', handleMapMovement);
      map.on('zoomend', handleMapMovement);

      if (mapInitialized && (showLocations || showGeofences)) {
        handleMapMovement();
        setMapInitialized(false);
      }

      // Clean up listeners on unmount
      return () => {
        map.off('moveend', handleMapMovement);
        map.off('zoomend', handleMapMovement);
      };
    }
  }, [map, fetchMapItemsWithBounds]);

  //Show items on map after the fetch is completed
  useEffect(() => {
    if (activateShowGeofences && !isFetchingGeofences) {
      dispatch(geofenceActions.showAllGeofencesInMap());
      setActivateShowGeofences(false);
    }
  }, [activateShowGeofences, isFetchingGeofences]);

  useEffect(() => {
    if (showGeofences) setActivateShowGeofences(true);
  }, [mapGeofencesOptionsToolbar?.membership?.organizationId]);

  const [spiderfyActivated, setSpiderfyActivated] = useState<boolean>(false);
  const [spiderfyState, setSpiderfyState] = useState<boolean>(false);

  /* -------------------------------------------------------------------------- */
  /*                           Map Bounds and rerender                          */
  /* -------------------------------------------------------------------------- */
  useDeepCompareEffect(() => {
    if (
      !mapObjectsBoundsOptions.setMapPositionToGeofenceBounds &&
      !mapObjectsBoundsOptions.setMapPositionToLocationBounds
    ) {
      if (center) setMapPosition(center);
    }
  }, [center || []]);

  useEffect(() => {
    if (map != null) {
      setTimeout(function () {
        map.invalidateSize(true);
      }, 100);
    }
  }, [map, rerenderMap]);

  useEffect(() => {
    // @ts-ignore locale does return lang
    if (map) map.pm.setLang(intl.locale);
  }, [map, intl]);

  // Componente para manejar eventos del mapa
  const MapEvents = () => {
    const map = useMapEvents({
      load: () => {
        setMap(map);
        const bounds = map.getBounds();
        const boundsFilter = {
          minLat: bounds.getSouthWest().lat,
          minLng: bounds.getSouthWest().lng,
          maxLat: bounds.getNorthEast().lat,
          maxLng: bounds.getNorthEast().lng,
          showInMapLocations: showLocations,
          showInMapGeofences: showGeofences
        };
        if (showLocations || showGeofences) fetchMapItemsWithBounds(boundsFilter);
      },
      zoomend: () => {
        const currentZoom = map.getZoom();
        if (currentZoom >= 16 && !spiderfyState) {
          setSpiderfyState(true);
          setSpiderfyActivated(true);
        } else if (currentZoom < 16 && spiderfyState) {
          setSpiderfyActivated(true);
          setSpiderfyState(false);
        }
      }
    });
    return null;
  };

  useEffect(() => {
    return () => {
      // Cleanup mejorado
      if (map) {
        map.remove();
      }
    };
  }, [map]);

  return (
    <div>
      <MapProvider>
        <MapContainer
          center={mapPosition}
          preferCanvas={true}
          zoom={initialZoom}
          className={className}
          style={{ ...style }}
        >
          <MapBaseLayers
            {...mapBaseLayersOptions}
            listenMapControlChanges={listenMapControlChangesLayers}
          />
          <MapEvents />
          {/* MapLocations */}
          {(mapLocationsOptionsToolbar.initialLocationInformationOpenId !== undefined ||
            (showLocations && mapLocationsOptionsToolbar.useIsShownLocation)) && (
            <MapLocations
              isSelectedSomething={isSelectedSomething}
              setIsSelectedSomething={setIsSelectedSomething}
              {...mapLocationsOptionsToolbar}
              showLocations={showLocations}
              useSlidingpanelLocationInformation={
                mapLocationsOptions?.useSlidingpanelLocationInformation ?? true
              }
              locations={mapLocationsOptionsToolbar?.locations}
              setEditingLocationParent={setEditingLocation}
              editingLocationParent={editingLocation}
              editingLocationParentRef={editingLocationRef}
              startClusteringAt={zoomClusterLimit}
              zoomLimitSet={isClusterLimitSet}
              resetMapHome={resetMapHome}
              spiderfyProps={{
                spiderfyActivated: spiderfyActivated,
                setSpiderfyActivated: setSpiderfyActivated,
                spiderfyState: spiderfyState
              }}
              onExecuteEditLocation={() => {
                setEditingLocation(undefined);
                if (mapLocationsOptionsToolbar?.setFetchLocations)
                  mapLocationsOptionsToolbar?.setFetchLocations(true);
              }}
            />
          )}
          {mapLocationsOptions.showLocations && <MapLocations {...mapLocationsOptions} />}
          {mapLocationEditOptionsToolbar?.showLocation && (
            <MapLocationEdit {...mapLocationEditOptionsToolbar} />
          )}
          {mapLocationEditOptions.showLocation && <MapLocationEdit {...mapLocationEditOptions} />}
          {/* Geofences */}
          {(showGeofences || mapGeofencesOptionsToolbar.useIsShownGeofence) && (
            <MapGeofences
              isSelectedSomething={isSelectedSomething}
              setIsSelectedSomething={setIsSelectedSomething}
              {...mapGeofencesOptionsToolbar}
              showGeofences={showGeofences}
              useSlidingpanelGeofenceInformation={true}
              geofences={geofences}
              setMapPositionToGeofenceBounds={false}
              setEditingGeofenceParent={setEditingGeofence}
              editingGeofenceParent={editingGeofence}
              editingGeofenceParentRef={editingGeofenceRef}
              startClusteringAt={startClusteringAt}
              resetMapHome={resetMapHome}
            />
          )}
          {mapGeofencesOptions.showGeofences && <MapGeofences {...mapGeofencesOptions} />}
          {mapGeofenceEditOptionsToolbar?.showGeofence && (
            <MapGeofenceEdit {...mapGeofenceEditOptionsToolbar} />
          )}
          {mapGeofenceEditOptions.showGeofence && <MapGeofenceEdit {...mapGeofenceEditOptions} />}
          {/* MapRoutes */}
          {(showRoutes || mapRoutesOptionsToolbar.useIsShownRoute) && (
            <MapRoutes
              isSelectedSomething={isSelectedSomething}
              setIsSelectedSomething={setIsSelectedSomething}
              {...mapRoutesOptionsToolbar}
              showRoutes={showRoutes}
              useSlidingPaneRouteInformation={true}
              routes={routes}
              setEditingRouteParent={setEditingRoute}
              editingRouteParent={editingRoute}
            />
          )}
          {mapRoutesOptions.showRoutes && <MapRoutes {...mapRoutesOptions} />}
          {editingRoute?.showRoute && (
            <MapRouteEdit setEditingRouteParent={setEditingRoute} {...editingRoute} />
          )}
          {/* MapUnits */}
          {(mapUnitsOptionsToolbar.initialUnitInformationOpenId !== undefined ||
            showUnits ||
            mapUnitsOptionsToolbar.useIsShownUnit) && (
            <MapUnits
              isSelectedSomething={isSelectedSomething}
              setIsSelectedSomething={setIsSelectedSomething}
              {...mapUnitsOptionsToolbar}
              showUnits={showUnits}
              useSlidingpanelUnitInformation={true}
              units={
                //!mapUnitsOptionsToolbar.initialUnitInformationOpenId
                mapUnitsOptionsToolbar.units
                //: [selectedUnit]
              }
              setSelectedUnit={setSelectedUnit}
              selectedUnit={selectedUnit}
              selectedUnitRef={selectedUnitRef}
              startClusteringAt={zoomClusterLimit}
              startRerender={startRerender}
              zoomLimitSet={isClusterLimitSet}
              resetMapHome={resetMapHome}
              spiderfyProps={{
                spiderfyActivated: spiderfyActivated,
                setSpiderfyActivated: setSpiderfyActivated,
                spiderfyState: spiderfyState
              }}
              showTrajectory={showTrajectory}
              setShowTrajectory={setShowTrajectory}
            />
          )}
          {mapUnitsOptions.showUnits && (
            <MapUnits
              {...mapUnitsOptions}
              setSelectedUnit={setSelectedUnit}
              selectedUnit={selectedUnit}
              selectedUnitRef={selectedUnitRef}
              startClusteringAt={startClusteringAt}
            />
          )}
          {mapUnitsPublicOptions && mapUnitsPublicOptions.showUnits && (
            <MapUnitsPublic {...mapUnitsPublicOptions} />
          )}
          {/* Map Object Bounds */}
          <MapObjectsBounds
            isSelectedSomething={isSelectedSomething}
            {...mapObjectsBoundsOptions}
          />
          {/* Toolbars */}
          {homeToolbarOptions.useToolbar && (
            <ToolbarHomeControl
              {...homeToolbarOptions}
              listenMapControlChanges={listenMapControlChanges}
            />
          )}
          {authSelectors.getAuthUserHasPermissionsAny(store, ['LO008', 'LO001']) &&
            locationsToolbarOptions.useToolbar && (
              <ToolbarLocationsControl
                {...locationsToolbarOptions}
                showLocations={showLocations}
                setShowLocations={(value) => setShowLocations(value)}
                listenMapControlChanges={listenMapControlChanges}
              />
            )}
          {authSelectors.getAuthUserHasPermissionsAny(store, ['GF001', 'GF002']) &&
            geofencesToolbarOptions.useToolbar && (
              <ToolbarGeofencesControl
                {...geofencesToolbarOptions}
                showGeofences={showGeofences}
                setShowGeofences={(value) => setShowGeofences(value)}
                listenMapControlChanges={listenMapControlChanges}
              />
            )}
          {authSelectors.getAuthUserHasPermissionsAny(store, ['UP025']) &&
            unitsToolbarOptions.useToolbar && (
              <ToolbarUnitsControl
                {...unitsToolbarOptions}
                showUnits={showUnits}
                setSelectedUnit={setSelectedUnit}
                setShowUnits={(value) => setShowUnits(value)}
                listenMapControlChanges={listenMapControlChanges}
              />
            )}
          {authSelectors.getAuthUserHasPermissionsAny(store, ['RL001']) &&
            routesToolbarOptions.useToolbar && (
              <ToolbarRoutesControl
                {...routesToolbarOptions}
                showRoutes={showRoutes}
                setEditingRouteParent={setEditingRoute}
                setShowRoutes={(value) => setShowRoutes(value)}
                listenMapControlChanges={listenMapControlChanges}
              />
            )}{' '}
          {(authSelectors.getAuthUserHasPermissionsAny(store, ['UP046']) ||
            authSelectors.getAuthUser(store).unitTrajectory == 1) &&
            trajectoryToolbarOptions.useToolbar && (
              <ToolbarUnitTrajectoryControl
                {...trajectoryToolbarOptions}
                showTrajectory={showTrajectory}
                setShowTrajectory={(value) => setShowTrajectory(value)}
                listenMapControlChanges={listenMapControlChanges}
              />
            )}
          {unitLabelsToolbarOptions.useToolbar && (
            <ToolbarUnitLabelsControl
              {...unitLabelsToolbarOptions}
              listenMapControlChanges={listenMapControlChanges}
            />
          )}
          {trackUnitWindowOptions?.useToolbar && (
            <ToolbarTrackUnitWindow
              {...trackUnitWindowOptions}
              listenMapControlChanges={listenMapControlChanges}
            />
          )}
          {streetViewToolbarOptions.useToolbar && (
            <ToolbarStreetViewControl
              {...streetViewToolbarOptions}
              listenMapControlChanges={listenMapControlChanges}
            />
          )}
          {rulerControlOptions.useControl && (
            <RulerControl
              {...rulerControlOptions}
              listenMapControlChanges={listenMapControlChanges}
            />
          )}
        </MapContainer>
      </MapProvider>
    </div>
  );
};

MapsBasic.defaultProps = {
  mapOptions: {
    center: [51.5074, 0.1278],
    zoom: 13,
    style: {},
    className: '',
    rerenderMap: null
  },

  homeToolbarOptions: ToolbarHomeControl.defaultProps,
  locationsToolbarOptions: ToolbarLocationsControl.defaultProps,
  geofencesToolbarOptions: ToolbarGeofencesControl.defaultProps,
  routesToolbarOptions: ToolbarRoutesControl.defaultProps,
  unitsToolbarOptions: ToolbarUnitsControl.defaultProps,
  unitLabelsToolbarOptions: ToolbarUnitLabelsControl.defaultProps,
  rulerControlOptions: RulerControl.defaultProps,
  trajectoryToolbarOptions: ToolbarUnitTrajectoryControl.defaultProps,
  streetViewToolbarOptions: ToolbarStreetViewControl.defaultProps,
  mapBaseLayersOptions: MapBaseLayers.defaultProps,
  mapLocationsOptionsToolbar: MapLocations.defaultProps,
  mapLocationsOptions: MapLocations.defaultProps,
  mapLocationEditOptions: MapLocationEdit.defaultProps,
  mapGeofencesOptionsToolbar: MapGeofences.defaultProps,
  mapGeofencesOptions: MapGeofences.defaultProps,
  mapGeofenceEditOptions: MapGeofenceEdit.defaultProps,
  mapRoutesOptionsToolbar: MapRoutes.defaultProps,
  mapRoutesOptions: MapRoutes.defaultProps,
  mapRouteEditOptions: MapRouteEdit.defaultProps,
  mapUnitsOptionsToolbar: MapUnits.defaultProps,
  mapUnitsOptions: MapUnits.defaultProps,
  mapObjectsBoundsOptions: MapObjectsBounds.defaultProps
};
export default MapsBasic;
